import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { METRICS_CONFIG } from '../../config/metrics';
import BlockedSiteHelp from './BlockedSiteHelp';
import {
  runQuickAudit,
  startComplexAuditServer,
  getComplexAuditStatus,
  findRunningComplexAudit,
  cancelComplexAudit,
  downloadStoredAuditReport,
  hasQuickAuditAccess,
  hasDeepTestAccess,
  downloadHTMLFile,
  auditUrlsMatch
} from '../../utils/quickAudit';
import DomainAuditPanel from './DomainAuditPanel';
import { getComplexAuditInvitationModel } from '../../utils/complexAuditInvitation';
import { getDeepFailurePresentation } from '../../utils/deepFailurePresentation';
import { buildDiagnosticCandidates, formatTrendSummary } from '../../utils/metricDiagnosticCandidates';

const DEVICE_LABELS = {
  PHONE: { cs: 'Mobil', en: 'Mobile' },
  DESKTOP: { cs: 'Desktop', en: 'Desktop' },
  TABLET: { cs: 'Tablet', en: 'Tablet' },
  ALL: { cs: 'Všechna zařízení', en: 'All devices' }
};

const DeviceIcon = ({ device, size = 14, className = '' }) => {
  if (device === 'PHONE') return <Icons.Phone size={size} className={className} />;
  if (device === 'DESKTOP') return <Icons.Desktop size={size} className={className} />;
  if (device === 'TABLET') return <Icons.Tablet size={size} className={className} />;
  return <Icons.AllDevices size={size} className={className} />;
};

const formatMetricValue = (metric, value) => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  const config = METRICS_CONFIG[metric];
  if (config?.format) return config.format(num);
  if (metric === 'CLS') return num.toFixed(3);
  return `${Math.round(num)}${config?.unit || ''}`;
};

const statusStyles = {
  good: 'bg-green-500/15 text-green-400 border-green-500/30',
  unknown: 'bg-slate-700/30 text-slate-400 border-slate-600',
  poor: 'bg-red-500/20 text-red-400 border-red-500/30',
  'needs-improvement': 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

const itemKey = (item) => `${item.metric}::${item.device}`;

export const QuickAuditButton = ({
  failingVitals = [],
  candidates,
  url,
  user,
  onOpenAuth,
  onOpenPricing,
  compact = false
}) => {
  const { language } = useLanguage();
  const [showModal, setShowModal] = useState(false);

  if (!url) return null;

  const label = language === 'cs' ? 'Diagnostika metrik' : 'Metric diagnostics';

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={compact
          ? 'cv-audit-action inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-red-500/20 to-amber-500/20 text-amber-300 border border-amber-500/40 hover:from-red-500/30 hover:to-amber-500/30 transition-colors'
          : 'cv-audit-action inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/20 to-amber-500/20 text-amber-300 border border-amber-500/40 hover:from-red-500/30 hover:to-amber-500/30 transition-colors'}
        title={language === 'cs'
          ? 'Vyberte metriku a zařízení k diagnostice'
          : 'Choose a metric and device to diagnose'}
      >
        <Icons.Zap size={compact ? 13 : 16} />
        {label}
        {failingVitals.length > 0 && <span className={`inline-flex items-center justify-center rounded-full bg-red-500/30 text-red-300 font-bold ${compact ? 'text-[10px] px-1.5' : 'text-xs px-2 py-0.5'}`}>
          {failingVitals.length}
        </span>}
      </button>
      {showModal && (
        <QuickAuditModal
          failingVitals={failingVitals}
          candidates={candidates}
          url={url}
          user={user}
          onClose={() => setShowModal(false)}
          onOpenAuth={onOpenAuth}
          onOpenPricing={onOpenPricing}
        />
      )}
    </>
  );
};

export const ComplexAuditInvitation = ({
  failingVitals,
  candidates,
  url,
  user,
  onOpenAuth,
  onOpenPricing,
  isLoading = false
}) => {
  const { language } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const model = getComplexAuditInvitationModel({ language, url, isLoading, failingVitals });

  if (!model) return null;

  return (
    <>
      <aside
        data-complex-audit-invitation
        className="relative mt-8 overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-slate-900/70 to-blue-500/10 p-5 sm:p-6 shadow-lg shadow-cyan-950/20 animate-slide-in"
      >
        <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 max-w-3xl">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-cyan-300">
                <Icons.Activity size={12} />
                {model.eyebrow}
              </span>
              <span className="text-xs font-medium text-amber-300">{model.issueCount}</span>
            </div>
            <h3 className="text-xl font-bold text-white sm:text-2xl">{model.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{model.description}</p>
            <div className="mt-3 flex min-w-0 flex-col gap-2 text-xs text-slate-400 sm:flex-row sm:items-center sm:gap-4">
              <span className="inline-flex items-center gap-1.5">
                <Icons.Clock size={13} className="text-cyan-400" />
                {model.runtime}
              </span>
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <Icons.Globe size={13} className="shrink-0 text-cyan-400" />
                <span className="truncate" title={model.url}>{model.url}</span>
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            aria-label={model.ctaLabel}
            aria-haspopup="dialog"
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:shadow-cyan-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 sm:w-auto"
          >
            <Icons.Zap size={16} />
            {model.cta}
            <Icons.ArrowRight size={15} />
          </button>
        </div>
      </aside>
      {showModal && (
        <QuickAuditModal
          failingVitals={failingVitals}
          candidates={candidates}
          url={model.url}
          user={user}
          onClose={() => setShowModal(false)}
          onOpenAuth={onOpenAuth}
          onOpenPricing={onOpenPricing}
        />
      )}
    </>
  );
};

export const MetricDiagnosticModal = ({
  failingVitals = [],
  candidates,
  initialSelection,
  url,
  user,
  onClose,
  onOpenAuth,
  onOpenPricing
}) => {
  const { language } = useLanguage();
  const diagnosticItems = candidates || buildDiagnosticCandidates(null).map(item =>
    failingVitals.find(failing => itemKey(failing) === itemKey(item))
      ? { ...failingVitals.find(failing => itemKey(failing) === itemKey(item)), reason: 'failing' }
      : item
  );
  const [selected, setSelected] = useState(() => new Set(
    initialSelection ? initialSelection.map(itemKey) : failingVitals.length === 1 ? [itemKey(failingVitals[0])] : []
  ));
  const [activeRun, setActiveRun] = useState(null); // complex | individual
  const running = activeRun !== null;
  const complexRunning = activeRun === 'complex';
  const individualRunning = activeRun === 'individual';
  const [restoring, setRestoring] = useState(true);
  const [restoreError, setRestoreError] = useState(false);
  const [pollError, setPollError] = useState(false);
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState({});
  // Default ON: the complex audit auto-accepts common CMPs so the lab
  // measures the page in the state real (consenting) users see it.
  const [skipCmp, setSkipCmp] = useState(true);
  const [complexState, setComplexState] = useState(null); // { stage, metric, index, total } | { stage:'done'|'error', ... }
  // Last event seen per stage — drives the phase checklist so the user can
  // see which analysis is running, which finished and which is still ahead.
  const [complexStages, setComplexStages] = useState({});
  const [complexResult, setComplexResult] = useState(null);
  // Queue id of the SERVER-SIDE complex audit — the job runs in the server
  // queue worker and survives closing this tab; we only poll its progress.
  const [complexQueueId, setComplexQueueId] = useState(null);
  const cancelledRef = useRef(false);
  const pollTimerRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    // React Strict Mode performs a development-only setup → cleanup → setup
    // cycle. Reset the flag during setup so the real mount keeps polling.
    unmountedRef.current = false;
    return () => {
      // Closing the modal stops POLLING only — the server job keeps running
      // and stays reachable via Moje analýzy → Fronta analýz.
      unmountedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const isLoggedIn = !!user;
  const hasAccess = hasQuickAuditAccess(user);
  const deepAllowed = hasDeepTestAccess(user);

  const cs = language === 'cs';

  const toggle = (key) => {
    if (running) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const stageLabel = (stage, downloaded) => {
    const labels = {
      pending: cs ? 'Čeká…' : 'Waiting…',
      deep: cs ? 'Probíhá hloubkový test (~1 min)…' : 'Running deep test (~1 min)…',
      diagnostics: cs ? 'Načítám diagnostiku…' : 'Fetching diagnostics…',
      narrative: cs ? 'Generuji AI shrnutí…' : 'Generating AI summary…',
      generating: cs ? 'Vytvářím report…' : 'Building report…',
      done: downloaded
        ? (cs ? 'Hotovo — staženo' : 'Done — downloaded')
        : (cs ? 'Hotovo' : 'Done'),
      'done-partial': cs ? 'Hotovo (bez hloubkového testu)' : 'Done (without deep test)',
      'done-limited': cs ? 'Hotovo — neúplný report!' : 'Done — incomplete report!',
      error: cs ? 'Chyba' : 'Error'
    };
    return labels[stage] || '';
  };

  // Default flow: the complex audit is enqueued as a SERVER-SIDE job
  // (analysis_queue type 'complex-audit'). The server orchestrates all
  // phases — the audit finishes even if this tab is closed. The client only
  // polls progress_data and offers the finished report for download.
  const applyStatusRow = (row) => {
    if (!row || unmountedRef.current) return false;
    // Queue ids from stale/legacy responses must never make another site's
    // progress or result look like the audit currently open in this modal.
    if ((row.analysis_type && row.analysis_type !== 'complex-audit') || !auditUrlsMatch(row.url, url)) {
      setComplexState({
        stage: 'error',
        message: cs
          ? 'Stav auditu nelze bezpečně připojit k tomuto webu. Otevřete správný audit v Moje analýzy.'
          : 'This audit status cannot be safely attached to this website. Open the correct audit in My analyses.',
        code: 'AUDIT_URL_MISMATCH'
      });
      setActiveRun(null);
      setComplexQueueId(null);
      return false;
    }
    const status = row.status;
    if (status === 'pending') {
      setComplexState({ stage: 'queued' });
      return true;
    }
    if (status === 'running') {
      const pd = typeof row.progress_data === 'string'
        ? (() => { try { return JSON.parse(row.progress_data); } catch (_) { return null; } })()
        : row.progress_data;
      if (pd?.current) setComplexState(pd.current);
      else setComplexState({ stage: 'priority' });
      if (pd?.stages) setComplexStages(pd.stages);
      return true;
    }
    if (status === 'awaiting_review') {
      const pd = typeof row.progress_data === 'string'
        ? (() => { try { return JSON.parse(row.progress_data); } catch (_) { return null; } })()
        : row.progress_data;
      if (pd?.stages) setComplexStages(pd.stages);
      setComplexState({ stage: 'awaiting_review' });
      setComplexResult(null); // Private report is not downloadable before approval.
      setActiveRun(null);
      setComplexQueueId(null);
      return false;
    }
    if (status === 'completed') {
      // Sync the final stage history first: the last phases (AI summary,
      // report build) often finish between two polls, and without this the
      // checklist would wrongly show them as "skipped" on a successful run.
      const pdFinal = typeof row.progress_data === 'string'
        ? (() => { try { return JSON.parse(row.progress_data); } catch (_) { return null; } })()
        : row.progress_data;
      if (pdFinal?.stages) setComplexStages(pdFinal.stages);
      const rd = typeof row.result_data === 'string'
        ? (() => { try { return JSON.parse(row.result_data); } catch (_) { return null; } })()
        : row.result_data;
      setComplexResult({
        labAuditId: rd?.labAuditId,
        filename: rd?.filename,
        metric: rd?.metric,
        failedMetrics: rd?.failedMetrics || [],
        deepFailures: rd?.deepFailures || {},
        deepFailureContext: rd?.deepFailureContext || {},
        pendingMetrics: rd?.pendingMetrics || [],
        diagnosticsFailed: !!rd?.diagnosticsFailed
      });
      setComplexState({ stage: 'done' });
      setActiveRun(null);
      setComplexQueueId(null);
      return false;
    }
    if (status === 'failed') {
      // A job can cross several short final phases between polls. Use the
      // terminal row's durable history before rendering the failure, otherwise
      // a report-persistence error can be mislabelled as a stability failure.
      const pdFailed = typeof row.progress_data === 'string'
        ? (() => { try { return JSON.parse(row.progress_data); } catch (_) { return null; } })()
        : row.progress_data;
      if (pdFailed?.stages) setComplexStages(pdFailed.stages);
      const msg = row.error_message || '';
      setComplexState({
        stage: 'error',
        message: msg || (cs ? 'Audit selhal.' : 'The audit failed.'),
        code: /neodpovídá|not responding|nepodařilo změřit|could not be measured/i.test(msg) ? 'SITE_UNREACHABLE' : undefined
      });
      setActiveRun(null);
      setComplexQueueId(null);
      return false;
    }
    if (status === 'cancelled') {
      setComplexState({ stage: 'error', message: cs ? 'Audit byl zrušen.' : 'The audit was cancelled.' });
      setActiveRun(null);
      setComplexQueueId(null);
      return false;
    }
    return true;
  };

  // Reopening this modal attaches to the existing server job, never POSTs
  // merely to discover whether an audit is already running.
  useEffect(() => {
    let disposed = false;
    setRestoring(true);
    setRestoreError(false);
    if (!isLoggedIn || !hasAccess) {
      setRestoring(false);
      return;
    }
    findRunningComplexAudit(url).then(row => {
      if (disposed || !row) return;
      setActiveRun('complex');
      if (applyStatusRow(row)) setComplexQueueId(row.id);
    }).catch(() => {
      if (!disposed) setRestoreError(true);
    }).finally(() => {
      if (!disposed) setRestoring(false);
    });
    return () => { disposed = true; };
  }, [isLoggedIn, hasAccess, user?.id, url]);

  // One poll at a time, with an immediate refresh when the tab becomes
  // visible again. Effect-local disposal also handles Strict Mode remounts.
  useEffect(() => {
    if (!complexQueueId) return;
    let disposed = false;
    let inFlight = false;
    const refresh = async () => {
      if (disposed || inFlight) return;
      clearTimeout(pollTimerRef.current);
      inFlight = true;
      let keepPolling = true;
      try {
        const row = await getComplexAuditStatus(complexQueueId);
        if (!disposed) {
          setPollError(!row);
          if (row) keepPolling = applyStatusRow(row.analysis || row);
        }
      } catch (err) {
        if (!disposed) setPollError(true);
      } finally {
        inFlight = false;
        if (!disposed && keepPolling) pollTimerRef.current = setTimeout(refresh, 4000);
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      disposed = true;
      clearTimeout(pollTimerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [complexQueueId, cs]);

  const handleRunComplex = async () => {
    if (running || restoring || complexState?.stage === 'awaiting_review') return;
    cancelledRef.current = false;
    setActiveRun('complex');
    setPollError(false);
    setComplexResult(null);
    setComplexStages({});
    setComplexState({ stage: 'queued' });
    try {
      const { queueId } = await startComplexAuditServer({
        url,
        skipCmp: deepAllowed ? skipCmp : false,
        failingVitals,
        language
      });
      setComplexQueueId(queueId);
    } catch (err) {
      if (err?.queueId
        && ['pending', 'running', 'awaiting_review'].includes(err.activeStatus)
        && auditUrlsMatch(err.activeUrl, url)) {
        // startComplexAuditServer exposes queueId only after URL/type/status
        // metadata proved this is the same requested audit. Verify again at
        // the UI boundary so no future/legacy caller can make this blind.
        setComplexQueueId(err.queueId);
        return;
      }
      console.error('Complex CWV audit enqueue error:', err);
      setComplexState({ stage: 'error', message: err?.message, code: err?.code });
      setActiveRun(null);
    }
  };

  const handleCancelComplex = async () => {
    if (!complexQueueId) return;
    const ok = window.confirm(cs
      ? 'Opravdu zrušit běžící komplexní audit? Dosavadní měření se zahodí.'
      : 'Really cancel the running complex audit? Measurements so far will be discarded.');
    if (!ok) return;
    try {
      await cancelComplexAudit(complexQueueId);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      setComplexState({ stage: 'error', message: cs ? 'Audit byl zrušen.' : 'The audit was cancelled.' });
      setActiveRun(null);
      setComplexQueueId(null);
    } catch (err) {
      console.error('Complex audit cancel error:', err);
    }
  };

  const complexStageLabel = (s) => {
    if (!s) return '';
    if (s.stage === 'queued') return cs ? 'Ve frontě na serveru…' : 'Queued on the server…';
    if (s.stage === 'priority') return cs ? 'Počítám priority metrik (CrUX)…' : 'Computing metric priorities (CrUX)…';
    if (s.stage === 'deep') {
      const dev = s.device ? (cs
        ? (s.device === 'phone' ? ' · mobil' : s.device === 'tablet' ? ' · tablet' : ' · desktop')
        : (s.device === 'phone' ? ' · mobile' : ` · ${s.device}`)) : '';
      return cs
        ? `Hloubkový test ${s.metric}${dev} (${s.index}/${s.total}, ~1–2 min)…`
        : `Deep ${s.metric} test${dev} (${s.index}/${s.total}, ~1–2 min)…`;
    }
    if (s.stage === 'multipage') return cs
      ? `Vícestránkové měření ${s.metric} (${s.index}/${s.total}, až ~5 min)…`
      : `Multi-page ${s.metric} sweep (${s.index}/${s.total}, up to ~5 min)…`;
    if (s.stage === 'sweepdeep') return cs
      ? `Hloubkový test ${s.metric} na nejhorší stránce ze sweepu (${s.index}/${s.total}, ~1–2 min)…`
      : `Deep ${s.metric} test on the worst sweep page (${s.index}/${s.total}, ~1–2 min)…`;
    if (s.stage === 'stability') return cs
      ? `Ověřuji stabilitu ${s.metric} — pokus ${s.attempt}/${s.maxAttempts}, platná měření ${s.valid}/${s.target}…`
      : `Checking ${s.metric} stability — attempt ${s.attempt}/${s.maxAttempts}, valid measurements ${s.valid}/${s.target}…`;
    if (s.stage === 'diagnostics') return cs ? 'Načítám diagnostiku…' : 'Fetching diagnostics…';
    if (s.stage === 'narrative') return cs ? 'Generuji AI shrnutí…' : 'Generating AI summary…';
    if (s.stage === 'generating') return cs ? 'Skládám komplexní report…' : 'Building the complete report…';
    if (s.stage === 'done') return cs ? 'Hotovo — report uložen v Moje analýzy' : 'Done — report saved in My analyses';
    if (s.stage === 'awaiting_review') return cs
      ? 'Měření dokončeno — report je uložen a čeká na schválení administrátorem.'
      : 'Measurement complete — the saved report is awaiting administrator approval.';
    if (s.stage === 'error') return s.message || (cs ? 'Chyba' : 'Error');
    return cs ? 'Audit pokračuje na serveru…' : 'Audit continues on the server…';
  };

  // Phase checklist for the complex audit — canonical stage order matches
  // runComplexAudit's onStage events. A phase is "done" once a later phase
  // has started; phases never entered by then were skipped (e.g. no metric
  // above threshold → no sweep→deep round).
  const COMPLEX_PHASES = ['priority', 'deep', 'multipage', 'sweepdeep', 'stability', 'diagnostics', 'narrative', 'generating'];
  const complexPhaseTitle = (p) => ({
    priority: cs ? 'Priority metrik (CrUX × zařízení)' : 'Metric priorities (CrUX × device)',
    deep: cs ? 'Hloubkové testy metrik nad prahem' : 'Deep tests of above-threshold metrics',
    multipage: cs ? 'Vícestránkové měření (sweep)' : 'Multi-page sweep',
    sweepdeep: cs ? 'Hloubkový test nejhorší stránky ze sweepu' : 'Deep test of the worst sweep page',
    stability: cs ? 'Opakovaná měření — ověření stability' : 'Repeated measurements — stability check',
    diagnostics: cs ? 'Diagnostika' : 'Diagnostics',
    narrative: cs ? 'AI shrnutí' : 'AI summary',
    generating: cs ? 'Sestavení reportu' : 'Report assembly'
  })[p];
  // Human-readable explanation for each machine skip reason.
  const sweepDeepSkipLabel = (reason) => {
    const labels = {
      no_deep_endpoint: cs
        ? ' — přeskočeno (pro tuto metriku hloubkový test neexistuje)'
        : ' — skipped (no deep test available for this metric)',
      no_sweep_data: cs
        ? ' — přeskočeno (vícestránkový sweep nevrátil žádné stránky)'
        : ' — skipped (the multi-page sweep returned no pages)',
      no_valid_sweep_measurements: cs
        ? ' — přeskočeno (sweep nezachytil žádné platné měření; nuly a chybějící interakce nejsou dobré INP)'
        : ' — skipped (the sweep captured no valid measurement; zeroes and missing interactions are not good INP)',
      no_candidates_above_threshold: cs
        ? ' — přeskočeno (žádná stránka sweepu nepřekročila práh)'
        : ' — skipped (no sweep page exceeded the threshold)',
      entry_is_worst: cs
        ? ' — přeskočeno (nejhorší stránka sweepu je vstupní stránka)'
        : ' — skipped (the worst sweep page is the entry page)',
      budget_exhausted: cs
        ? ' — přeskočeno (vypršel časový rozpočet auditu)'
        : ' — skipped (audit time budget exhausted)',
      deep_not_allowed: cs
        ? ' — přeskočeno (hloubkový test vyžaduje tarif Premium nebo vyšší)'
        : ' — skipped (deep test requires Premium plan or higher)',
      no_valid_seed: cs
        ? ' — přeskočeno (chybí platné výchozí měření)'
        : ' — skipped (no valid seed measurement)',
      already_enough: cs
        ? ' — přeskočeno (dostatek platných opakování už byl naměřen)'
        : ' — skipped (enough valid repetitions already exist)',
    };
    return labels[reason] || (cs ? ' — přeskočeno' : ' — skipped');
  };

  const renderComplexChecklist = () => {
    if (!complexState || !Object.keys(complexStages).length) return null;
    const finished = complexState.stage === 'done' || complexState.stage === 'awaiting_review';
    const errored = complexState.stage === 'error';
    const curIdx = COMPLEX_PHASES.indexOf(complexState.stage);
    return (
      <div className="mt-2 space-y-1">
        {COMPLEX_PHASES.map((p, idx) => {
          const ev = complexStages[p];
          let status; // done | running | pending | skipped | failed | not-executed
          // An event with status:'skipped' means the orchestrator explicitly
          // recorded a skip reason — honour it regardless of audit state.
          if (ev?.status === 'skipped') {
            status = 'skipped';
          } else if (finished) {
            status = ev ? 'done' : 'skipped';
          } else if (errored) {
            status = ev ? (p === Object.keys(complexStages).pop() ? 'failed' : 'done') : 'not-executed';
          } else if (idx < curIdx) {
            status = ev ? 'done' : 'skipped';
          } else if (idx === curIdx) {
            status = 'running';
          } else {
            status = 'pending';
          }
          const icon = status === 'done' ? '✓' : status === 'running' ? '●' : status === 'failed' ? '✕' : status === 'skipped' ? '—' : status === 'not-executed' ? '×' : '○';
          const color = status === 'done' ? 'text-green-400' : status === 'running' ? 'text-cyan-300' : status === 'failed' ? 'text-red-400' : 'text-slate-500';
          const detail = status === 'running' && ev && ev.total
            ? ` (${ev.index}/${ev.total}${ev.metric ? ` · ${ev.metric}` : ''})`
            : status === 'done' && ev && ev.total ? ` (${ev.total}×)` : '';
          const skippedLabel = status === 'skipped'
            ? ((p === 'sweepdeep' || p === 'stability') && ev?.skipReason
              ? sweepDeepSkipLabel(ev.skipReason)
              : (cs ? ' — přeskočeno' : ' — skipped'))
            : status === 'not-executed'
              ? (cs ? ' — neprovedeno kvůli předchozí chybě' : ' — not executed due to an earlier error')
              : '';
          return (
            <div key={p} className={`flex items-center gap-2 text-xs ${color}`}>
              <span className={`w-3 text-center ${status === 'running' ? 'animate-pulse' : ''}`}>{icon}</span>
              <span>{complexPhaseTitle(p)}{detail}{skippedLabel}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const handleRun = async () => {
    if (running || restoring || selected.size === 0) return;
    cancelledRef.current = false;
    setActiveRun('individual');

    const items = diagnosticItems.filter(item => selected.has(itemKey(item)));
    const initial = {};
    items.forEach(item => { initial[itemKey(item)] = 'pending'; });
    setProgress(initial);
    setResults({});

    // Auto-download only a single report — multiple automatic downloads
    // trigger the browser's "allow multiple downloads" permission popup.
    const single = items.length === 1;

    for (const item of items) {
      if (cancelledRef.current) break;
      const key = itemKey(item);
      try {
        const { deepFailed, diagnosticsFailed, html, filename } = await runQuickAudit({
          url,
          metric: item.metric,
          device: item.device,
          fieldValue: item.value,
          auditContext: { reason: item.reason, trend: item.trend || null },
          user,
          language,
          skipCmp: deepAllowed ? skipCmp : false,
          autoDownload: single,
          onStage: (stage) => setProgress(prev => ({ ...prev, [key]: stage }))
        });
        setResults(prev => ({ ...prev, [key]: { html, filename, downloaded: single, diagnosticsFailed } }));
        setProgress(prev => ({
          ...prev,
          [key]: diagnosticsFailed ? 'done-limited' : (deepFailed && deepAllowed ? 'done-partial' : 'done')
        }));
      } catch (err) {
        console.error('Quick audit error:', err);
        // Keep the thrown message so the row can show WHY it failed (e.g. the
        // reachability preflight: "site unavailable / blocks measurement").
        setResults(prev => ({ ...prev, [key]: { errorMessage: err?.message, errorCode: err?.code } }));
        setProgress(prev => ({ ...prev, [key]: 'error' }));
        // A site that failed the reachability preflight will fail it for every
        // selected metric — don't repeat the wait for the remaining rows.
        if (err?.code === 'SITE_UNREACHABLE') {
          setProgress(prev => {
            const next = { ...prev };
            items.forEach(it => { if (next[itemKey(it)] === 'pending') next[itemKey(it)] = 'error'; });
            return next;
          });
          break;
        }
      }
    }

    setActiveRun(null);
  };

  const handleDownload = (key) => {
    const r = results[key];
    if (!r?.html) return;
    downloadHTMLFile(r.html, r.filename);
    setResults(prev => ({ ...prev, [key]: { ...prev[key], downloaded: true } }));
  };

  const handleClose = () => {
    // The complex audit runs SERVER-SIDE: closing the modal never cancels
    // it — the job keeps running and is reachable via Moje analýzy.
    // Only the legacy single-metric audits still run in this tab, so only
    // those need the confirm-before-throwing-away-measurement guard.
    if (individualRunning) {
      const ok = window.confirm(cs
        ? 'Analýza stále běží. Zavřením okna ji zrušíte a bude nutné ji spustit znovu. Opravdu zrušit?'
        : 'The analysis is still running. Closing this window cancels it and it will have to be re-run. Really cancel?');
      if (!ok) return;
      cancelledRef.current = true;
    }
    onClose();
  };

  // Backdrop clicks never close this modal. During a run an accidental
  // click must not cancel a long measurement, and after a run users select
  // and copy text from the checklist — a click landing on the backdrop must
  // not dismiss their results. Closing is explicit: X / Close buttons only.
  const modal = (
    <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="bg-slate-900 border-2 border-amber-500/40 rounded-2xl p-6 max-w-lg w-full shadow-2xl shadow-amber-500/10 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-audit-modal-title"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/30 to-amber-500/30 flex items-center justify-center border border-amber-500/40">
              <Icons.Zap size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 id="quick-audit-modal-title" className="text-lg font-bold text-white">
                {cs ? 'Diagnostika metrik' : 'Metric diagnostics'}
              </h3>
              <p className="text-xs text-slate-400">{url}</p>
            </div>
          </div>
          <button onClick={handleClose} aria-label={cs ? 'Zavřít diagnostiku' : 'Close diagnostics'} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <Icons.Close size={18} />
          </button>
        </div>

        {!isLoggedIn ? (
          <div className="text-center py-6">
            <p className="text-slate-300 mb-4">
              {cs
                ? 'Pro spuštění auditu se prosím zaregistrujte nebo přihlaste.'
                : 'Please register or log in to run an audit.'}
            </p>
            <button
              onClick={() => { handleClose(); onOpenAuth && onOpenAuth('register'); }}
              className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              {cs ? 'Registrovat zdarma' : 'Register for free'}
            </button>
          </div>
        ) : !hasAccess ? (
          <div className="text-center py-6">
            <p className="text-slate-300 mb-4">
              {cs
                ? 'Audit s diagnostikou je dostupný od tarifu Pro.'
                : 'Audits with diagnostics are available from the Pro plan.'}
            </p>
            <button
              onClick={() => { handleClose(); onOpenPricing && onOpenPricing(); }}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              {cs ? 'Zobrazit tarify' : 'View plans'}
            </button>
          </div>
        ) : (
          <>
            {/* Default: unified CWV audit — one run, all metrics, deep layers by priority */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/40 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Icons.Zap size={16} className="text-cyan-400" />
                <span className="text-sm font-bold text-white">
                  {cs ? 'Komplexní CWV audit — závěrečný report' : 'Complete CWV audit — final report'}
                </span>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                {cs
                  ? 'Jedno spuštění: seřadí všechny metriky podle priority (CrUX × podíl zařízení) a pro každou nad prahem přidá hloubkovou vrstvu — příčiny, vlastníky a zadání k předání v jednom reportu. Metriky bez hloubkové vrstvy (TTFB/FCP) mají v reportu připravené místo se základními čísly.'
                  : 'One run: ranks all metrics by priority (CrUX × device share) and adds a deep layer for every metric above threshold — causes, owners and handover tasks in a single report. Metrics without a deep layer yet (TTFB/FCP) get a prepared slot with basic numbers.'}
              </p>
              <p className="text-xs text-cyan-200 mb-3">
                {cs
                  ? 'Toto je hlavní audit: běží na serveru i po zavření okna a report se uloží do Mých analýz. Zvolené dílčí metriky níže jej nenahrazují.'
                  : 'This is the main audit: it runs on the server even after closing this window and saves the report in My analyses. The individual metric checks below do not replace it.'}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={handleRunComplex}
                  disabled={running || restoring || complexState?.stage === 'awaiting_review'}
                  className="px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                >
                  {complexRunning ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icons.Zap size={14} />
                  )}
                  {restoring
                    ? (cs ? 'Načítám stav auditu…' : 'Loading audit status…')
                    : complexState?.stage === 'awaiting_review'
                      ? (cs ? 'Čeká na schválení' : 'Awaiting approval')
                    : complexRunning
                      ? (cs ? 'Probíhá komplexní audit…' : 'Complete audit running…')
                      : (cs ? 'Spustit komplexní audit' : 'Run complete audit')}
                </button>
                {complexState && !individualRunning && (
                  <span className={`text-xs ${complexState.stage === 'error' ? 'text-red-400' : complexState.stage === 'done' ? 'text-green-400' : 'text-cyan-300'}`}>
                    {complexStageLabel(complexState)}
                  </span>
                )}
                {running && complexQueueId && (
                  <button
                    type="button"
                    onClick={handleCancelComplex}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500/20 transition-colors"
                  >
                    {cs ? 'Zrušit audit' : 'Cancel audit'}
                  </button>
                )}
                {(complexResult?.html || complexResult?.labAuditId) && (
                  <button
                    type="button"
                    onClick={() => complexResult.html
                      ? downloadHTMLFile(complexResult.html, complexResult.filename)
                      : downloadStoredAuditReport(complexResult.labAuditId, complexResult.filename).catch(err => console.error('Report download error:', err))}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors ${complexResult.failedMetrics?.length > 0
                      ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25'
                      : 'bg-green-500/15 text-green-300 border border-green-500/40 hover:bg-green-500/25'}`}
                    title={complexResult.filename}
                  >
                    <Icons.Download size={12} />
                    {complexResult.failedMetrics?.length > 0
                      ? (cs ? 'Stáhnout (nekompletní)' : 'Download (incomplete)')
                      : (cs ? 'Stáhnout report' : 'Download report')}
                  </button>
                )}
              </div>
              {(restoreError || pollError) && (
                <p role="status" className="mt-2 text-xs text-amber-300">
                  {cs
                    ? 'Aktuální stav se nepodařilo načíst. To neznamená, že audit skončil; zkontrolujte Frontu analýz. U běžícího auditu načítání automaticky opakujeme.'
                    : 'Could not load the current status. This does not mean the audit stopped; check the analysis queue. Status updates for a tracked audit retry automatically.'}
                </p>
              )}
              {renderComplexChecklist()}
              {running && complexQueueId && (
                <p className="text-xs text-slate-500 mt-2">
                  {cs
                    ? 'Audit běží na serveru — toto okno můžete klidně zavřít. Průběh najdete v Moje analýzy → Fronta analýz a po dokončení dostanete notifikaci.'
                    : 'The audit runs on the server — you can safely close this window. Track progress in My analyses → Analysis queue; you will be notified when it finishes.'}
                </p>
              )}
              {complexResult && (complexResult.failedMetrics?.length > 0 || complexResult.diagnosticsFailed) && (
                <div className="mt-3 space-y-2">
                  {complexResult.failedMetrics?.length > 0 && (() => {
                    const failedMetric = complexResult.metric || complexResult.failedMetrics[0];
                    const failure = complexResult.deepFailures?.[failedMetric];
                    const presentation = getDeepFailurePresentation(failure, language, {
                      metric: failedMetric,
                      hasSweep: !!complexResult.deepFailureContext?.hasSweep,
                      hasDiagnostics: !!complexResult.deepFailureContext?.hasDiagnostics
                    });
                    const fieldItem = failingVitals.find(item => item.metric === failedMetric);
                    const verdict = fieldItem?.status === 'poor'
                      ? (cs ? 'špatné' : 'poor')
                      : (cs ? 'potřebuje zlepšení' : 'needs improvement');
                    return (
                      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-left">
                        <p className="text-sm font-semibold text-red-200">{presentation.title}</p>
                        {fieldItem?.value != null && (
                          <p className="mt-1 text-xs text-gray-300">
                            <strong className="text-white">Field p75:</strong>{' '}
                            {formatMetricValue(failedMetric, fieldItem.value)} — {verdict}.
                            {' '}{cs
                              ? 'Field verdikt zůstává autoritativní.'
                              : 'The field verdict remains authoritative.'}
                          </p>
                        )}
                        <p className="mt-2 text-xs text-red-100">
                          <strong>{presentation.labels.cause}:</strong> {presentation.cause}
                        </p>
                        <p className="mt-1 text-xs text-gray-300">
                          <strong className="text-white">{presentation.labels.available}:</strong>{' '}
                          {presentation.available}
                        </p>
                        <p className="mt-1 text-xs text-gray-300">
                          <strong className="text-white">{presentation.labels.next}:</strong>{' '}
                          {presentation.action}
                        </p>
                      </div>
                    );
                  })()}
                  {complexResult.diagnosticsFailed && (
                    <p className="text-xs text-amber-400">
                      {cs
                        ? 'PSI diagnostika nebyla dostupná — report je omezen na dostupná field a laboratorní data.'
                        : 'PSI diagnostics were unavailable — the report is limited to the available field and lab data.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            <p className="text-sm text-slate-300 mb-4">
              {cs
                ? 'Nebo rychlý audit jedné metriky: vyberte, které chcete auditovat — pro každou se spustí analýza a hotový audit (HTML) si stáhnete tlačítkem u dané položky.'
                : 'Or a quick single-metric audit: pick the ones to audit — each runs an analysis and you download the ready-made audit (HTML) via the button on each row.'}
            </p>

            <div className="space-y-2 mb-4">
              {['failing', 'trend', 'manual'].map(reason => {
                const items = diagnosticItems.filter(item => item.reason === reason);
                if (!items.length) return null;
                const Container = reason === 'manual' ? 'details' : 'section';
                const Heading = reason === 'manual' ? 'summary' : 'h4';
                return <Container key={reason} className="space-y-2" open={reason === 'manual' && initialSelection?.some(item => item.reason === 'manual') ? true : undefined}>
                  <Heading className={`text-sm font-semibold text-slate-300 py-2 ${reason === 'manual' ? 'cursor-pointer' : ''}`}>
                    {reason === 'failing' ? (cs ? 'Nesplněné limity' : 'Failing thresholds')
                      : reason === 'trend' ? (cs ? 'Zhoršující se metriky' : 'Deteriorating metrics')
                        : (cs ? 'Další metriky a zařízení · ruční výběr' : 'Other metrics and devices · manual selection')} ({items.length})
                  </Heading>
                  {items.map(item => {
                const key = itemKey(item);
                const state = progress[key];
                const checked = selected.has(key);
                return (
                  <label
                    key={key}
                    className={`flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? 'bg-slate-800 border-amber-500/40' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'} ${running ? 'cursor-default' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(key)}
                      disabled={running}
                      className="w-4 h-4 accent-amber-500"
                    />
                    <span className="font-bold text-white w-12">{item.metric}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
                      <DeviceIcon device={item.device} className="text-slate-400" />
                      {DEVICE_LABELS[item.device]?.[cs ? 'cs' : 'en'] || item.device}
                    </span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[item.status] || statusStyles.unknown}`}>
                      {item.value == null ? (cs ? 'Bez field dat' : 'No field data') : formatMetricValue(item.metric, item.value)}
                    </span>
                    {item.trend && <span className="w-full text-xs leading-relaxed text-slate-400">{formatTrendSummary(item, language)}</span>}
                    {state && (
                      <span className={`text-xs ${state === 'error' && results[key]?.errorMessage ? '' : 'whitespace-nowrap'} ${state === 'error' ? 'text-red-400' : state === 'done-limited' ? 'text-amber-400' : state === 'done' || state === 'done-partial' ? 'text-green-400' : 'text-cyan-400'}`}>
                        {(state !== 'done' && state !== 'done-partial' && state !== 'done-limited' && state !== 'error' && state !== 'pending') && (
                          <span className="inline-block w-3 h-3 mr-1 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin align-middle" />
                        )}
                        {state === 'error' && results[key]?.errorMessage
                          ? results[key].errorMessage
                          : stageLabel(state, results[key]?.downloaded)}
                      </span>
                    )}
                    {(state === 'done' || state === 'done-partial' || state === 'done-limited') && results[key]?.html && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload(key); }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-green-500/15 text-green-300 border border-green-500/40 hover:bg-green-500/25 transition-colors whitespace-nowrap"
                        title={results[key].filename}
                      >
                        <Icons.Download size={12} />
                        {cs ? 'Stáhnout' : 'Download'}
                      </button>
                    )}
                  </label>
                );
                  })}</Container>;
              })}
            </div>

            {(complexState?.code === 'SITE_UNREACHABLE'
              || Object.values(results).some(r => r?.errorCode === 'SITE_UNREACHABLE')) && (
              <div className="mb-4">
                <BlockedSiteHelp cs={cs} url={url} />
              </div>
            )}

            {!deepAllowed && (
              <p className="text-xs text-slate-500 mb-4">
                {cs
                  ? 'Hloubkový laboratorní test (Playwright) je součástí tarifů Premium a Enterprise — audit bude vygenerován z PageSpeed diagnostiky.'
                  : 'The deep lab test (Playwright) is part of Premium and Enterprise plans — the audit will be generated from PageSpeed diagnostics.'}
              </p>
            )}
            {deepAllowed && (
              <>
                <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700 mb-3">
                  <div>
                    <div className="text-sm text-slate-200 font-medium">
                      {cs ? 'Přeskočit cookie banner' : 'Skip cookie banner'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {cs
                        ? 'Pokusí se automaticky akceptovat běžné CMP (Didomi, Cookiebot, OneTrust) — stejná volba jako u ručního hloubkového testu.'
                        : 'Attempts to auto-accept common CMPs (Didomi, Cookiebot, OneTrust) — same option as in the manual deep test.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={skipCmp}
                    aria-label={cs ? 'Přeskočit cookie banner' : 'Skip cookie banner'}
                    onClick={() => !running && setSkipCmp(v => !v)}
                    disabled={running}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${skipCmp ? 'bg-purple-600' : 'bg-slate-600'} ${running ? 'opacity-50' : ''}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${skipCmp ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  {cs
                    ? 'Každý audit zahrnuje hloubkový laboratorní test (~1–2 min na metriku a zařízení). Komplexní audit navíc přidá vícestránkové měření — celkově počítejte i s 10–20 minutami podle počtu metrik nad prahem.'
                    : 'Each audit includes a deep lab test (~1–2 min per metric and device). The complex audit additionally runs multi-page sweeps — the whole run can take 10–20 minutes depending on how many metrics are above threshold.'}
                </p>
                <DomainAuditPanel url={url} disabled={running || restoring} />
              </>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleRun}
                disabled={running || restoring || selected.size === 0}
                className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-red-500 to-amber-500 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {individualRunning ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {cs ? 'Probíhá dílčí diagnostika…' : 'Individual diagnostics running…'}
                  </>
                ) : (
                  <>
                    <Icons.Zap size={16} />
                    {cs
                      ? `Spustit dílčí diagnostiku (${selected.size})`
                      : `Run individual diagnostics (${selected.size})`}
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors text-sm"
              >
                {cs ? 'Zavřít' : 'Close'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

const QuickAuditModal = MetricDiagnosticModal;
export default QuickAuditButton;
